import DOMPurify, { type Config as DOMPurifyConfig } from "dompurify";

/** Escape plain text before interpolating into HTML template strings. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const RICH_HTML_TAGS = [
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "a",
  "strong",
  "em",
  "u",
  "s",
  "blockquote",
  "pre",
  "code",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "div",
  "span",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
] as const;

const RICH_HTML_ATTR = ["href", "target", "rel", "class", "style"] as const;

/** Agent messages, chat, guardians, meridian prose, ops tooling. */
export const RICH_HTML_SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [...RICH_HTML_TAGS],
  ALLOWED_ATTR: [...RICH_HTML_ATTR],
  ADD_ATTR: ["target"],
};

/** Screener filter pills — only the styled column label span is expected. */
const FILTER_PILL_SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: ["span"],
  ALLOWED_ATTR: ["class"],
};

/** TipTap / report-builder output (images, mentions, tables). */
const REPORT_HTML_SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    ...RICH_HTML_TAGS,
    "img",
    "colgroup",
    "col",
    "mark",
    "sup",
    "sub",
  ],
  ALLOWED_ATTR: [
    ...RICH_HTML_ATTR,
    "src",
    "alt",
    "width",
    "height",
    "colspan",
    "rowspan",
    "data-type",
    "data-id",
    "data-category",
    "data-path",
    "data-resource-slot-id",
    "data-date-format",
    "data-sources",
  ],
  ADD_ATTR: ["target"],
};

const NUMBERED_ITEM_RE = /^\d+\.\s+/;

function isNumberedItem(text: string): boolean {
  return NUMBERED_ITEM_RE.test(text.trim());
}

function stripNumberPrefix(text: string): string {
  return text.trim().replace(NUMBERED_ITEM_RE, "").replace(/\n/g, " ");
}

function stripChatLayoutClasses(html: string): string {
  return html.replace(/\sclass="([^"]*)"/gi, (_match, classes: string) => {
    const trimmed = classes
      .split(/\s+/)
      .filter((token) => token && !/^m[trblxy]?-\d/.test(token))
      .join(" ");
    return trimmed ? ` class="${trimmed}"` : "";
  });
}

function joinPlainTextBlocks(blocks: string[]): string {
  const out: string[] = [];
  let listItems: string[] = [];
  let proseBlocks: string[] = [];

  const flushProse = () => {
    if (proseBlocks.length === 0) return;
    out.push(`<p>${proseBlocks.map((block) => block.replace(/\n/g, "<br>")).join("<br><br>")}</p>`);
    proseBlocks = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    flushProse();
    out.push(`<ol>${listItems.map((item) => `<li>${item}</li>`).join("")}</ol>`);
    listItems = [];
  };

  for (const block of blocks) {
    const lines = block.split(/\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length >= 2 && lines.every(isNumberedItem)) {
      flushList();
      out.push(
        `<ol>${lines.map((line) => `<li>${stripNumberPrefix(line)}</li>`).join("")}</ol>`,
      );
      continue;
    }

    if (lines.length === 1 && isNumberedItem(lines[0])) {
      listItems.push(stripNumberPrefix(lines[0]));
      continue;
    }

    flushList();
    proseBlocks.push(block);
  }

  flushList();
  flushProse();
  return out.join("");
}

function compactChatBlockHtml(html: string): string {
  let result = stripChatLayoutClasses(html);

  while (/<\/p>\s*<p(?:\s[^>]*)?>/gi.test(result)) {
    result = result.replace(/<\/p>\s*<p(?:\s[^>]*)?>/gi, "<br><br>");
  }

  return result.replace(/<p(?:\s[^>]*)?>\s*<\/p>/gi, "");
}

/** Normalize common LLM chat HTML before sanitization (legacy tags, literal newlines). */
export function normalizeRichChatHtml(content: string | null | undefined): string {
  if (!content) return "";

  let html = content;
  html = html.replace(/<b(\s[^>]*)?>/gi, "<strong$1>");
  html = html.replace(/<\/b>/gi, "</strong>");
  html = html.replace(/<i(\s[^>]*)?>/gi, "<em$1>");
  html = html.replace(/<\/i>/gi, "</em>");

  if (html.includes("\n")) {
    const hasBlockTags = /<(p|ul|ol|li|h[1-6]|div|table|pre|blockquote)\b/i.test(html);
    if (!hasBlockTags) {
      const blocks = html.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
      html = joinPlainTextBlocks(blocks);
    } else {
      // Newlines between tags are just how the HTML was printed. A newline inside the text is a line break.
      html = html.trim().replace(/>\s*\n+\s*</g, "><").replace(/\n/g, "<br>");
    }
  }

  return compactChatBlockHtml(html);
}

export function sanitizeRichHtml(html: string | null | undefined): string {
  return String(DOMPurify.sanitize(html ?? "", RICH_HTML_SANITIZE_CONFIG));
}

export function sanitizeChatMessageHtml(content: string | null | undefined): string {
  return sanitizeRichHtml(normalizeRichChatHtml(content));
}

export function sanitizeFilterPillHtml(html: string | null | undefined): string {
  return String(DOMPurify.sanitize(html ?? "", FILTER_PILL_SANITIZE_CONFIG));
}

export function sanitizeReportHtml(html: string | null | undefined): string {
  return String(DOMPurify.sanitize(html ?? "", REPORT_HTML_SANITIZE_CONFIG));
}
